import { useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { useLocation } from 'react-router';
import {
  ArrowLeft,
  CloudDownload,
  Menu,
  TriangleAlert,
  UploadCloud,
} from 'lucide-react';
import { networkStatusAtom } from '../../store/network/networkState';
import { hasUpdateAtom } from '../../store/update/updateState';
import useStaticImage from '../../hooks/useStaticImage';
import { usePendingDeliveryCount } from '../../hooks/usePendingDeliveryCount';
import { UpdateModal } from './UpdateModal';
import { workboxFoundAtom } from '../../store/network/indexhtmlState';
import { usePWAStatus } from '../../pwa';
import { v4 } from 'uuid';
import { shouldHideBack } from './headerNav';
import { Button } from '../atoms/Button';
import { formatCount } from '../../helpers/syncResultHelpers';

interface HeaderProps {
  onBack: () => void;
  onMenuToggle: () => void;
}

export function Header({ onBack, onMenuToggle }: HeaderProps) {
  const location = useLocation();
  const isOnline = useAtomValue(networkStatusAtom);
  const hasUpdate = useAtomValue(hasUpdateAtom);
  const indexHTMLFound = useAtomValue(workboxFoundAtom);
  const hideBack = shouldHideBack(location.pathname);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [timeoutId] = useState<string>(v4());
  const { status: pwaStatus } = usePWAStatus({ timeoutId: timeoutId });

  // The update chip does not wait for the online status: "online" means the API
  // answers (#11623), and an API-side fault must not hide the update that could
  // fix an installed build (#10001). The dialog checks the app host itself.
  const showOfflineIcon = useMemo(() => !isOnline, [isOnline]);
  const showUpdateIcon = useMemo(() => {
    const indexNotFound = !indexHTMLFound && pwaStatus === 'ready';
    return hasUpdate || indexNotFound;
  }, [hasUpdate, indexHTMLFound, pwaStatus]);

  // Vector wordmark (crisp at any DPI). The legacy bradken_logo_small.png was
  // retired: its visible art was only 87x15 inside a 177x32 canvas, so it
  // always rendered half-size for its box and soft on high-DPI screens.
  const bradkenLogoImage = useStaticImage('images/bradken_wordmark.svg');
  // Event-driven only (mount / online / foreground / drain / submit-marker event) - a
  // per-route refresh re-materialised the whole nested inspections store on every
  // navigation, which is the exact whole-store-scan pattern the perf pass removed.
  const pendingDeliveryCount = usePendingDeliveryCount();

  return (
    <header className="flex items-center gap-3 border-b border-line p-4">
      {/* Hamburger far-left, in the SAME spot as the drawer's own hamburger
          (insite's trick): the drawer covers this bar when open, and its top
          row re-renders this control white-on-navy at identical coordinates,
          so opening reads as the chrome restyling around a stationary control.
          Geometry contract with Drawer's top row: p-4 origin, ONE 32px (h-8)
          control axis, gap-3, h-8 wordmark - keep the two in lockstep. (On
          non-prod the environment banner offsets the bar by its own height;
          production aligns exactly.) */}
      <button
        onClick={onMenuToggle}
        aria-label="Open navigation menu"
        className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-sm transition-colors hover:bg-brandLight/30 active:bg-brandLight/50"
      >
        <Menu className="size-6" />
      </button>
      {/* The bar shows Back OR the logo, never both (26 Aug 2026). The old
          always-on centred logo squeezed into a zero-gap mis-tap zone between
          Back and the menu at phone widths, and its Link-to-Home bypassed the
          navigation blockers that guard Back. Where Back is hidden (Home and
          submit) the slot is free, so the brand shows there instead - a
          plain image at true size, deliberately NOT a link, aligned with the
          drawer's wordmark so it too stays put when the drawer opens. */}
      {!hideBack ? (
        <Button
          variant="ghost"
          className="h-8 cursor-pointer text-brand"
          onClick={onBack}
        >
          <ArrowLeft className="size-5" />
          Back
        </Button>
      ) : (
        <img src={bradkenLogoImage} alt="Bradken" className="h-8 w-auto" />
      )}
      {/* Status indicators, right-anchored via ml-auto; their width flips
          can't shift anything since nothing sits to their right. */}
      <div className="ml-auto flex items-center justify-end gap-4">
        {/* Durable-delivery backlog: submitted inspections not yet fully on the server */}
        {pendingDeliveryCount > 0 && (
          <div
            className="flex items-center gap-1 rounded-sm bg-brandOrange px-2 py-1 text-white"
            title={`${formatCount(pendingDeliveryCount, 'inspection')} waiting to sync`}
          >
            <UploadCloud size={20} />
            <span className="text-sm font-bold">{pendingDeliveryCount}</span>
          </div>
        )}

        {/* Offline indicator (red triangle) */}
        {showOfflineIcon && (
          <div
            className="rounded-sm bg-red-500 p-1"
            role="status"
            aria-label="You are offline - changes are saved on this device"
            title="You are offline - changes are saved on this device"
          >
            <TriangleAlert className="text-white" size={24} fill="red" />
          </div>
        )}

        {/* Update available: a new release is ready. Amber = the warning
            colour role (yellow retired); CloudDownload pairs with the
            UploadCloud pending-sync chip and says "release", where the old
            grey alert triangle read as a vague error. */}
        {showUpdateIcon && (
          <button
            onClick={() => setShowUpdateModal(true)}
            title="New version available - tap to update"
            className="flex items-center gap-1 rounded-sm bg-amber-500 px-2 py-1 transition-colors hover:bg-amber-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 active:bg-amber-700"
          >
            <CloudDownload className="shrink-0 text-white" size={20} />
            <span className="text-sm font-bold text-white">
              Update available
            </span>
          </button>
        )}
      </div>

      {/* Update Modal */}
      <UpdateModal
        isOpen={showUpdateModal}
        onClose={() => setShowUpdateModal(false)}
      />
    </header>
  );
}
